/**
 * Airport code → city name map for display in the dashboard.
 * Inverted from email-parser's CITY_TO_AIRPORT + international hubs.
 * Only covers airports relevant to AA/DL/UA routes.
 */

const AIRPORT_CITIES: Record<string, string> = {
  // Major hubs
  ATL: 'Atlanta',
  BOS: 'Boston',
  BWI: 'Baltimore',
  CHS: 'Charleston',
  CLE: 'Cleveland',
  CLT: 'Charlotte',
  CMH: 'Columbus',
  CVG: 'Cincinnati',
  DCA: 'Washington',
  DTW: 'Detroit',
  EWR: 'Newark',
  FLL: 'Fort Lauderdale',
  IAD: 'Washington',
  IAH: 'Houston',
  IND: 'Indianapolis',
  JAX: 'Jacksonville',
  JFK: 'New York',
  LGA: 'New York',
  MCI: 'Kansas City',
  MCO: 'Orlando',
  MIA: 'Miami',
  MSP: 'Minneapolis',
  ORD: 'Chicago',
  ORF: 'Norfolk',
  PBI: 'West Palm Beach',
  PHL: 'Philadelphia',
  PIT: 'Pittsburgh',
  RDU: 'Raleigh',
  RIC: 'Richmond',
  RSW: 'Fort Myers',
  SAV: 'Savannah',
  SRQ: 'Sarasota',
  TPA: 'Tampa',

  // Central
  AUS: 'Austin',
  BHM: 'Birmingham',
  BNA: 'Nashville',
  DAL: 'Dallas',
  DFW: 'Dallas',
  HOU: 'Houston',
  MDW: 'Chicago',
  MEM: 'Memphis',
  MKE: 'Milwaukee',
  MSY: 'New Orleans',
  OKC: 'Oklahoma City',
  SAT: 'San Antonio',
  STL: 'St. Louis',

  // Mountain
  ABQ: 'Albuquerque',
  DEN: 'Denver',
  ELP: 'El Paso',
  PHX: 'Phoenix',
  SLC: 'Salt Lake City',
  TUS: 'Tucson',

  // Pacific
  BUR: 'Burbank',
  LAX: 'Los Angeles',
  LAS: 'Las Vegas',
  LGB: 'Long Beach',
  OAK: 'Oakland',
  ONT: 'Ontario',
  PDX: 'Portland',
  SAN: 'San Diego',
  SEA: 'Seattle',
  SFO: 'San Francisco',
  SJC: 'San Jose',
  SMF: 'Sacramento',
  SNA: 'Orange County',

  // Alaska / Hawaii
  ANC: 'Anchorage',
  HNL: 'Honolulu',
  OGG: 'Maui',
  LIH: 'Kauai',
  KOA: 'Kona',

  // Canada
  YYZ: 'Toronto',
  YUL: 'Montreal',
  YOW: 'Ottawa',
  YWG: 'Winnipeg',
  YYC: 'Calgary',
  YEG: 'Edmonton',
  YVR: 'Vancouver',
  YYJ: 'Victoria',

  // International hubs
  IST: 'Istanbul',
  LHR: 'London',
  LGW: 'London',
  CDG: 'Paris',
  ORY: 'Paris',
  FRA: 'Frankfurt',
  MUC: 'Munich',
  AMS: 'Amsterdam',
  MAD: 'Madrid',
  BCN: 'Barcelona',
  FCO: 'Rome',
  MXP: 'Milan',
  ZRH: 'Zurich',
  DUB: 'Dublin',
  NRT: 'Tokyo',
  HND: 'Tokyo',
  ICN: 'Seoul',
  PEK: 'Beijing',
  PVG: 'Shanghai',
  HKG: 'Hong Kong',
  SIN: 'Singapore',
  BKK: 'Bangkok',
  SYD: 'Sydney',
  MEL: 'Melbourne',
  CUN: 'Cancun',
  MEX: 'Mexico City',
  GRU: 'São Paulo',
  EZE: 'Buenos Aires',
  BOG: 'Bogota',
  LIM: 'Lima',
  SCL: 'Santiago',
  DOH: 'Doha',
  DXB: 'Dubai',
  TLV: 'Tel Aviv',
  DEL: 'Delhi',
  BOM: 'Mumbai',
  CPT: 'Cape Town',
  JNB: 'Johannesburg',
};

/** An airport entry for autocomplete display. */
export interface AirportEntry {
  code: string;
  city: string;
}

/** All known airports as a sorted list for autocomplete. */
export const AIRPORTS: AirportEntry[] = Object.entries(AIRPORT_CITIES)
  .map(([code, city]) => ({ code, city }))
  .sort((a, b) => a.code.localeCompare(b.code));

/**
 * Search airports by code or city name. Case-insensitive.
 * Returns up to `limit` matches, prioritizing code-prefix matches.
 */
export function searchAirports(query: string, limit = 8): AirportEntry[] {
  if (!query.trim()) return [];
  const q = query.toUpperCase().trim();

  const codeMatches: AirportEntry[] = [];
  const cityMatches: AirportEntry[] = [];

  for (const airport of AIRPORTS) {
    if (airport.code.startsWith(q)) {
      codeMatches.push(airport);
    } else if (airport.city.toUpperCase().includes(q)) {
      cityMatches.push(airport);
    }
    if (codeMatches.length + cityMatches.length >= limit) break;
  }

  return [...codeMatches, ...cityMatches].slice(0, limit);
}

/**
 * Get city name for an airport IATA code.
 * Returns undefined for unknown codes — caller should gracefully degrade.
 */
export function getCityName(code: string): string | undefined {
  return AIRPORT_CITIES[code.toUpperCase()];
}
